import { useDraggable } from '@dnd-kit/core';
import React, { forwardRef, memo, useMemo } from 'react';
import { useTheme } from 'styled-components';

import { AssetAction } from '../..';
import { Image, Text } from '../../../ui';
import {
  StyledBadge,
  StyledCardLink,
  StyledImageCardText,
  StyledImageWrapper,
  StyledWrapper,
} from './ImageCard.style';

export interface ImageCardDetailsProps {
  link?: string;
  maxHeight?: number | string;
  maxWidth?: number | string;
  selected?: boolean;
  disabled?: boolean;
}

export type ImageCardProps = {
  source: string | string[];
  details: unknown;
  data?: AssetAction['details'];
  title?: string;
  draggable?: boolean;
  active?: boolean;
  onClick?: (action: AssetAction) => void;
  extra?: React.ReactNode;
  badge?: string;
  lazy?: boolean;
};

export const ImageCard = memo(
  forwardRef(function ImageCard(
    {
      source,
      details,
      data,
      title,
      draggable,
      active = false,
      lazy = false,
      onClick = () => {
        return;
      },
      extra,
      badge,
    }: ImageCardProps,
    ref: React.Ref<HTMLImageElement>
  ) {
    const imageCardDetails = details as ImageCardDetailsProps;
    const theme = useTheme();
    const imageSource = source instanceof Array ? source[0] : source;
    const draggableHandlers = useDraggable({
      id: imageSource,
      data: {
        type: ['canvas'],
        source: imageSource,
        title,
        details: data,
      },
    });

    const draggableData = useMemo(() => {
      return draggable
        ? {
            ref: draggableHandlers.setNodeRef,
            ...draggableHandlers.listeners,
            ...draggableHandlers.attributes,
          }
        : {};
    }, [draggable, draggableHandlers]);

    return (
      <StyledWrapper
        {...draggableData}
        onClick={(event) => {
          event.stopPropagation();

          onClick({
            source: imageSource,
            action: 'click',
            details: data,
          });
        }}
        selected={imageCardDetails?.selected || active}
        disabled={imageCardDetails?.disabled}
      >
        <StyledCardLink href={imageCardDetails?.link}>
          {imageSource && (
            <StyledImageWrapper
              $maxHeight={imageCardDetails?.maxHeight}
              $maxWidth={imageCardDetails?.maxWidth}
            >
              <Image source={imageSource} ref={ref} draggable={draggable} lazy={lazy} />

              {badge && (
                <StyledBadge direction="bottom-left">
                  <Text size="Small" color={theme.Color.NeutralsFixed.Paper}>
                    {badge}
                  </Text>
                </StyledBadge>
              )}
              {extra}
            </StyledImageWrapper>
          )}
          {!!title && <StyledImageCardText children={title} size="BodyHeavy" />}
        </StyledCardLink>
      </StyledWrapper>
    );
  })
);
